import React from "react";
import { useTranslation } from "react-i18next";
import useDebounce from "src/ui/hooks/useDebounce";

import { Box, Typography } from "@mui/material";
import { showErrorToast } from "@shared/toasts/Toasts";
import { IDetectedBarcode, IScannerError, Scanner } from "@yudiel/react-qr-scanner";

import { scannerHeight, useStyles } from "./AddDeviceModal.styles";

type Props = {
    onScan: (rawValue: string) => void;
};

export const QrScanner = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation("modals");
    const [cameraError, setCameraError] = React.useState<string | null>(null);

    const getCameraErrorMessage = (error: IScannerError) => {
        switch (error.kind) {
            case "permission-denied":
                return t("addDevice.cameraError.permissionDenied");
            case "no-camera":
                return t("addDevice.cameraError.noCamera");
            case "in-use":
                return t("addDevice.cameraError.inUse");
            case "insecure-context":
                return t("addDevice.cameraError.insecureContext");
            default:
                return t("addDevice.cameraError.unknown", { message: error.message });
        }
    };

    const onError = (error: IScannerError) => {
        const message = getCameraErrorMessage(error);
        setCameraError(message);
        showErrorToast(message);
    };
    const onScan = useDebounce(
        (detectedCodes: IDetectedBarcode[]) => {
            const rawValue = detectedCodes[0]?.rawValue;
            if (rawValue) {
                props.onScan(rawValue);
            }
        },
        1000,
        { leading: true, trailing: false },
    );

    if (cameraError) {
        return (
            <Box className={classes.cameraError}>
                <i className="fa-solid fa-video-slash fa-2x" />
                <Typography>{cameraError}</Typography>
            </Box>
        );
    }

    return (
        <Scanner
            styles={{ video: { width: "100%", height: scannerHeight }, container: { width: "100%", height: scannerHeight } }}
            onScan={onScan}
            onError={onError}
            allowMultiple={false}
        />
    );
};
